import React, { useRef, useState, useMemo } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { formatStatus } from '../../data/validationData';
import { ValidationProject } from '../../types/validation';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

interface SummaryReportProps {
  projects: ValidationProject[];
}

const SummaryReport: React.FC<SummaryReportProps> = ({ projects }) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const cleanCount = projects.filter(p => p.status === 'clean').length;
  const stage1Count = projects.filter(p => p.status === 'stage1').length;
  const stage2Count = projects.filter(p => p.status === 'stage2').length;
  const totalCount = projects.length;
  const flaggedProjects = projects.filter(p => p.status !== 'clean');

  // Issue category counts
  const issueCounts = [
    { name: 'Missing Required Data', count: projects.filter(p => p.flags.some(f => f.type === 'Missing Required Fields')).length },
    { name: 'System vs Document Mismatch', count: projects.filter(p => p.flags.some(f => f.type === 'System vs Document Inconsistency')).length },
    { name: 'Requirement Adherence', count: projects.filter(p => p.flags.some(f => f.type === 'Requirement Adherence Issue')).length },
    { name: 'Benefit Duration Issues', count: projects.filter(p => p.flags.some(f => f.type === 'Benefit Duration Exceeded')).length },
    { name: 'Cost-Share Inconsistency', count: projects.filter(p => p.flags.some(f => f.type === 'Cost-Share Inconsistency')).length },
    { name: 'Material Change (YoY)', count: projects.filter(p => p.flags.some(f => f.type === 'Material Change vs Previous Year')).length },
  ];

  // Chart data for status distribution (Doughnut)
  const statusChartData = useMemo(() => ({
    labels: ['Clean / Ready', 'Stage 1 Flagged', 'Stage 2 Flagged'],
    datasets: [{
      data: [cleanCount, stage1Count, stage2Count],
      backgroundColor: ['#059669', '#f59e0b', '#dc2626'],
      borderWidth: 0,
    }],
  }), [cleanCount, stage1Count, stage2Count]);

  // Chart data for issue categories (Horizontal Bar)
  const issueChartData = useMemo(() => ({
    labels: issueCounts.map(i => i.name),
    datasets: [{
      label: 'Projects Affected',
      data: issueCounts.map(i => i.count),
      backgroundColor: '#06b6d4',
      borderRadius: 4,
    }],
  }), [issueCounts]);

  const doughnutOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { padding: 12, usePointStyle: true },
      },
    },
  }), []);

  const barOptions = useMemo(() => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { beginAtZero: true, grid: { color: '#f3f4f6' } },
      y: { grid: { display: false } },
    },
    plugins: { legend: { display: false } },
  }), []);

  const exportReport = () => {
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += 'Water Stewardship Validation Report - VWBA 2.0\n\n';
    csvContent += 'Summary Statistics\n';
    csvContent += `Total Projects,${totalCount}\n`;
    csvContent += `Clean / Ready for Approval,${cleanCount},${totalCount > 0 ? ((cleanCount / totalCount) * 100).toFixed(1) : 0}%\n`;
    csvContent += `Stage 1 Flagged,${stage1Count},${totalCount > 0 ? ((stage1Count / totalCount) * 100).toFixed(1) : 0}%\n`;
    csvContent += `Stage 2 Flagged,${stage2Count},${totalCount > 0 ? ((stage2Count / totalCount) * 100).toFixed(1) : 0}%\n\n`;
    csvContent += 'Flagged Projects Detail\n';
    csvContent += 'Project ID,Name,Type,Stage,Flag Reason,Risk Level\n';

    flaggedProjects.forEach(project => {
      const flagReasons = project.flags.map(f => f.type).join('; ');
      const riskLevel = project.flags[0]?.riskLevel || (project.status === 'stage2' ? 'High' : 'Medium');
      csvContent += `${project.id},"${project.name}",${project.type},${project.stage},"${flagReasons}",${riskLevel}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'validation_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPdf = async () => {
    if (!reportRef.current) return;
    setIsExportingPdf(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('validation_report.pdf');
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Summary Report - Validation Results</h2>
        <div className="flex gap-2">
          <button
            onClick={exportReport}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={exportPdf}
            disabled={isExportingPdf}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {isExportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Export PDF
          </button>
        </div>
      </div>

      <div ref={reportRef} className="space-y-4 bg-white p-2">

      {/* Validation Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Validation Summary</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-gray-600">Total Projects Validated:</span>
            <span className="font-semibold text-gray-900">{totalCount}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-100 bg-green-50 px-3 rounded">
            <span className="text-green-700">Clean / Ready for Approval:</span>
            <span className="font-semibold text-green-700">{cleanCount} ({totalCount > 0 ? ((cleanCount / totalCount) * 100).toFixed(0) : 0}%)</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-100 bg-amber-50 px-3 rounded">
            <span className="text-amber-700">Stage 1 Flagged:</span>
            <span className="font-semibold text-amber-700">{stage1Count} ({totalCount > 0 ? ((stage1Count / totalCount) * 100).toFixed(0) : 0}%)</span>
          </div>
          <div className="flex justify-between items-center py-2 bg-red-50 px-3 rounded">
            <span className="text-red-700">Stage 2 Flagged:</span>
            <span className="font-semibold text-red-700">{stage2Count} ({totalCount > 0 ? ((stage2Count / totalCount) * 100).toFixed(0) : 0}%)</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Validation Status Distribution</h3>
          <div className="h-64 flex items-center justify-center">
            <Doughnut data={statusChartData} options={doughnutOptions} />
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Issues by Category</h3>
          <div className="h-64">
            <Bar data={issueChartData} options={barOptions} />
          </div>
        </div>
      </div>

      {/* Top Issue Categories */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Issue Categories Summary</h3>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Issue Category</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {issueCounts.map((issue, index) => (
              <tr key={index}>
                <td className="px-4 py-2 text-sm text-gray-600">{issue.name}</td>
                <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">{issue.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Flagged Projects Detail */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Flagged Projects Detail ({flaggedProjects.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flag Reason</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {flaggedProjects.map(project => {
                const riskLevel = project.flags[0]?.riskLevel || (project.status === 'stage2' ? 'High' : 'Medium');
                return (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{project.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{project.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        project.status === 'stage1' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {formatStatus(project.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {project.flags.map(f => f.type).join(', ')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        project.status === 'stage2' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {riskLevel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SummaryReport;
